import { computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';

/** The i18n branch the ten category names live under, in `assets/i18n/{en,fr}.json`. */
const CATEGORY_KEY_PREFIX = 'ADMIN.ICON_CATEGORIES.';

/**
 * The teammate icon library, grouped and with its headings translated, ready for
 * `sd-icon-category-grid`.
 *
 * ## Why the app resolves these and the library does not
 *
 * `libs/serious-system/CLAUDE.md`: *"Components take pre-translated strings. The
 * caller owns i18n; the library owns none."* That is the whole reason, and it is
 * not about namespaces — all three hosts read the SAME `ADMIN.ICON_CATEGORIES.*`
 * branch. An earlier version of this code said otherwise in three places at once;
 * a maintainer who checked would have found the stated reason contradicted and
 * might have concluded the constraint was imaginary. It is not.
 *
 * ## Why it is a function and not three copies
 *
 * ⚠️ The `language()` read on the first line is load-bearing and easy to drop.
 * `instant()` subscribes to nothing, so a `computed()` that only called it would
 * memoize the boot-time language and a mid-session switch would leave the panel
 * in the old one forever — and no spec would see it, because the suites load no
 * dictionary. When this logic was copied per host, the third copy lost exactly
 * that line. One function is one place to get it right.
 *
 * Call it from an injection context (a field initializer or a constructor).
 *
 * Display order is the spec's *Translations* order — T1's generator says
 * explicitly: "do not sort".
 */
export function teammateIconSections(): Signal<
  fromSeriousSystem.SdIconCategorySection[]
> {
  const translate = inject(TranslateService);
  const language = toSignal(translate.onLangChange, { initialValue: null });

  return computed(() => {
    language();
    return fromSeriousSystem.TEAMMATE_ICON_CATEGORIES.map((category) => ({
      key: category.key,
      label: translate.instant(CATEGORY_KEY_PREFIX + category.key) as string,
      icons: category.icons,
    }));
  });
}
